import { NPVR_PLAYBACK_DEVICE_TYPE } from 'config/api';

import { Bookmark } from 'store/bookmarks/reducer.types';
import { StreamTypes } from 'store/player/actions.types';
import {
    EpgProgram,
    RBChannel,
    RBProgram,
    RBTechnical,
} from 'store/railsBuilder/reducer.types';
import {
    RecordingItem,
    RecordingMetadata,
} from 'store/recordings/reducer.types';

import getCatchupStream from 'services/data-manager/catchup/getCatchupStream';
import { TECHNICAL_DEVICE_TYPE } from 'services/data-manager/constants';
import getAssetTrickmodesControl from 'services/data-manager/parsing/utils/trickModesControl';
import { VideoItem, VodEditorial } from 'services/data-manager/types';

import getCorrectProgramStartAndEndTime from 'utils/getCorrectProgramStartAndEndTime';

import isStartoverAvailable from './utils/isStartoverAvailable';
import isTimeshiftAvailable from './utils/isTimeshiftAvailable';
import parseParentalRating from './utils/parseParentalRating';

type DistinctiveParams = Pick<
    VideoItem,
    | 'id'
    | 'title'
    | 'seriesId'
    | 'seriesTitle'
    | 'seasonNumber'
    | 'episodeNumber'
    | 'episodeTitle'
    | 'duration'
    | 'parentalRating'
    | 'originalProgram'
    | 'trickModesControl'
    | 'clientGlobalBlackoutControl'
    | 'clientMobileBlackoutControl'
    | 'clientWebBlackoutControl'
    | 'oohConfig'
    | 'drmId'
    | 'startTime'
    | 'endTime'
    | 'timeshiftAllowed'
    | 'startoverAllowed'
    | 'playbackUrls'
>;

type Params = {
    channel: RBChannel | undefined;
    program?: EpgProgram | RBProgram | VodEditorial;
    recording?: RecordingItem;
    recordingMetadata?: RecordingMetadata;
    streamType?: StreamTypes;
    bookmark?: Bookmark;
};

function parseVideoItem({
    channel,
    program,
    recording,
    recordingMetadata,
    bookmark,
    streamType,
}: Params): VideoItem {
    let distinctiveParams: DistinctiveParams | undefined;

    if (streamType === StreamTypes.vod) {
        const vod = program as VodEditorial;
        const vodPlayback = vod.technicals?.find(
            ({ deviceType }: RBTechnical) =>
                deviceType.includes(TECHNICAL_DEVICE_TYPE),
        )?.media?.AV_PlaylistName;

        distinctiveParams = {
            id: program?.id || '',
            title: program?.SeriesTitle || program?.Title,
            seriesId: program?.seriesRef,
            seriesTitle: program?.title,
            seasonNumber: program?.SeasonNumber || undefined,
            episodeNumber: program?.episodeNumber || undefined,
            episodeTitle: program?.EpisodeTitle || program?.Episode || '',
            duration: program?.period?.duration,
            parentalRating: parseParentalRating(program, channel),
            originalProgram: program,
            trickModesControl: getAssetTrickmodesControl(channel, program),
            clientGlobalBlackoutControl: program?.clientGlobalBlackoutControl,
            clientMobileBlackoutControl: program?.clientMobileBlackoutControl,
            clientWebBlackoutControl: program?.clientWebBlackoutControl,
            oohConfig: {
                [StreamTypes.catchup]: program?.clientMobileOOHCatchupControl,
                [StreamTypes.live]:
                    program?.clientMobileLiveOOHStreamingControl,
                [StreamTypes.startover]: program?.clientMobileRestartOOHControl,
                [StreamTypes.npvr]: program?.clientMobileOOHNpvrControl,
                [StreamTypes.vod]: program?.clientMobileVODOOHControl,
            },
            drmId: vodPlayback?.drmId,
            startTime: undefined,
            endTime: undefined,
            timeshiftAllowed: false,
            startoverAllowed: true,
            playbackUrls: [
                {
                    type: StreamTypes.vod,
                    url: vodPlayback?.uri || '',
                },
            ],
        };
    } else if (streamType === StreamTypes.npvr) {
        const npvrPlayback = NPVR_PLAYBACK_DEVICE_TYPE
            ? recording?.playback?.[NPVR_PLAYBACK_DEVICE_TYPE]
            : undefined;

        distinctiveParams = {
            id: recording?.contentId || '',
            title: recordingMetadata?.SeriesTitle || recordingMetadata?.Title,
            seriesId: recordingMetadata?.editorial?.seriesRef,
            seriesTitle: recordingMetadata?.title,
            seasonNumber: recordingMetadata?.SeasonNumber || undefined,
            episodeNumber: recordingMetadata?.episodeNumber || undefined,
            episodeTitle: recordingMetadata?.episode || '',
            duration: recordingMetadata?.period?.duration,
            parentalRating: parseParentalRating(recordingMetadata, channel),
            originalProgram: recording,
            trickModesControl: getAssetTrickmodesControl(
                channel,
                recordingMetadata,
            ),
            clientGlobalBlackoutControl:
                recordingMetadata?.clientGlobalBlackoutControl,
            clientMobileBlackoutControl:
                recordingMetadata?.clientMobileBlackoutControl,
            clientWebBlackoutControl:
                recordingMetadata?.clientWebBlackoutControl,
            oohConfig: {
                [StreamTypes.catchup]:
                    recordingMetadata?.clientMobileOOHCatchupControl,
                [StreamTypes.live]:
                    recordingMetadata?.clientMobileLiveOOHStreamingControl,
                [StreamTypes.startover]:
                    recordingMetadata?.clientMobileRestartOOHControl,
                [StreamTypes.npvr]:
                    recordingMetadata?.clientMobileOOHNpvrControl,
                [StreamTypes.vod]: recordingMetadata?.clientMobileVODOOHControl,
            },
            drmId: npvrPlayback?.drmId,
            startTime: recordingMetadata?.period?.start,
            endTime: recordingMetadata?.period?.end,
            timeshiftAllowed: false,
            startoverAllowed: true,
            playbackUrls: [
                {
                    type: StreamTypes.npvr,
                    url: npvrPlayback?.uri || '',
                },
            ],
        };
    } else {
        const { programStartTime, programEndTime } =
            getCorrectProgramStartAndEndTime(program);

        distinctiveParams = {
            id: recording?.contentId || program?.id || '',
            title: program?.SeriesTitle || program?.Title,
            seriesId: program?.seriesRef,
            seriesTitle: program?.title,
            seasonNumber: program?.SeasonNumber || undefined,
            episodeNumber: program?.episodeNumber || undefined,
            episodeTitle: program?.EpisodeTitle || program?.Episode || '',
            duration: program?.period?.duration,
            parentalRating: parseParentalRating(program, channel),
            originalProgram: program,
            trickModesControl: getAssetTrickmodesControl(channel, program),
            clientGlobalBlackoutControl: program?.clientGlobalBlackoutControl,
            clientMobileBlackoutControl: program?.clientMobileBlackoutControl,
            clientWebBlackoutControl: program?.clientWebBlackoutControl,
            oohConfig: {
                [StreamTypes.catchup]: program?.clientMobileOOHCatchupControl,
                [StreamTypes.live]:
                    program?.clientMobileLiveOOHStreamingControl,
                [StreamTypes.startover]: program?.clientMobileRestartOOHControl,
                [StreamTypes.npvr]: program?.clientMobileOOHNpvrControl,
                [StreamTypes.vod]: program?.clientMobileVODOOHControl,
            },
            drmId: channel?.technical?.drmId,
            startTime: programStartTime,
            endTime: programEndTime,
            timeshiftAllowed: isTimeshiftAvailable(channel, program),
            startoverAllowed: isStartoverAvailable(
                channel,
                program,
                streamType,
            ),
            playbackUrls: [
                {
                    type: StreamTypes.live,
                    url: channel?.technical.NetworkLocation || '',
                },
                {
                    type: StreamTypes.startover,
                    url: channel?.technical.StartOverLocation || '',
                },
                {
                    type: StreamTypes.catchup,
                    url: getCatchupStream(program),
                },
            ],
        };
    }

    return {
        channelId: channel?.editorial.id,
        channelTitle: channel?.editorial.longName,
        channelNumber: channel?.editorial.tvChannel,
        guardTime: channel?.editorial?.guardTime || 0,
        guardTimeEnd: channel?.editorial?.guardTimeEnd || 0,
        isFreeToAir: channel?.technical?.isFreeToAir,
        sourceConfig: { startOffset: bookmark?.position },
        ...distinctiveParams,
    };
}

export default parseVideoItem;
